import { Injectable } from "@angular/core";
import { UiSchemaCollection } from "@vp/formly/ui-schema-config";
import { OrganizationFeatures, RecordDataMapper } from "@vp/models";
import { deeperCopy } from "@vp/shared/utilities";
import { BehaviorSubject, Observable, of } from "rxjs";
import { map, mergeAll, scan, shareReplay } from "rxjs/operators";

type Feature<T> = {
  [K in keyof T]: { key: K; value: T[K] };
}[keyof T];

export interface FeatureConfig {
  [key: string]: any;
}

@Injectable({
  providedIn: "root"
})
export class FeatureService {
  /**
   * Publish (next) new added values to this observable it emits emits instances of observables
   * that are accumulated by feature$.
   */
  private features$$: BehaviorSubject<Observable<Feature<FeatureConfig>>> = new BehaviorSubject<
    Observable<Feature<FeatureConfig>>
  >(of());

  /**
   * mergeAll, scan here accumulates all of the features as they are added to the service
   * any subscribers will receive ALL features that have been added.
   */
  private features$: Observable<Feature<FeatureConfig>[]> = this.features$$.pipe(
    mergeAll(),
    scan((acc: Feature<FeatureConfig>[], filter: Feature<FeatureConfig>) => {
      acc.push(filter);
      return acc;
    }, [] as Feature<FeatureConfig>[]),
    shareReplay(1)
  );

  add(feature: FeatureConfig): void {
    this.features$$.next(
      of({
        key: feature.friendlyId,
        value: feature
      })
    );
  }

  // returns a specific feature from the accumulated features
  feature$ = (feature: string): Observable<FeatureConfig | null> => {
    return this.features$.pipe(
      map((features: FeatureConfig[]) => {
        return (
          deeperCopy(
            features.find((featureConfig: FeatureConfig) => {
              return featureConfig.key === feature;
            })?.value
          ) ?? null
        );
      })
    );
  };

  configurationLists$(feature: string): Observable<Record<string, string[]>> {
    return this.feature$(feature).pipe(
      map((featureConfig: FeatureConfig | null) => {
        return featureConfig !== null ? featureConfig["configurationLists"] : null;
      })
    );
  }

  configurationListValue$(feature: string, key: string): Observable<string[]> {
    return this.feature$(feature).pipe(
      map((featureConfig: FeatureConfig | null) => {
        return featureConfig !== null ? (featureConfig.configurationLists?.[key] ?? []) : [];
      })
    );
  }

  configurationValues$(feature: string): Observable<Record<string, string>> {
    return this.feature$(feature).pipe(
      map((featureConfig: FeatureConfig | null) => {
        return featureConfig !== null ? featureConfig["configurationValues"] : null;
      })
    );
  }

  configurationValuePrefix$(
    feature: string,
    prefix: string
  ): Observable<Record<string, string> | null> {
    return this.feature$(feature).pipe(
      map((featureConfig: FeatureConfig | null) => {
        if (featureConfig && featureConfig["configurationValues"]) {
          return Object.entries<string>(featureConfig["configurationValues"]).reduce(
            (acc: Record<string, string>, [key, value]: [string, string]) => {
              const keyParts = key.split(".");
              if (keyParts.length > 1 && keyParts.includes(prefix)) {
                acc[keyParts.slice(1, keyParts.length).join(".")] = value;
              }
              return acc;
            },
            {}
          );
        }
        return null;
      })
    );
  }

  configurationValue$(feature: string, key: string): Observable<string> {
    return this.feature$(feature).pipe(
      map((featureConfig: FeatureConfig | null) => {
        return featureConfig !== null ? (featureConfig.configurationValues?.[key] ?? null) : null;
      })
    );
  }

  configurationMappers$(feature: string): Observable<Record<string, RecordDataMapper[]>> {
    return this.feature$(feature).pipe(
      map((featureConfig: FeatureConfig | null) => {
        return featureConfig !== null ? featureConfig["configurationMappers"] : null;
      })
    );
  }

  configurationMapper$(feature: string, key: string): Observable<RecordDataMapper[]> {
    return this.feature$(feature).pipe(
      map((featureConfig: FeatureConfig | null) => {
        return featureConfig !== null ? featureConfig["configurationMappers"][key] : null;
      })
    );
  }

  configurationDictionaries$(feature: string): Observable<Record<string, unknown>> {
    return this.feature$(feature).pipe(
      map((featureConfig: FeatureConfig | null) => {
        return featureConfig !== null ? featureConfig["configurationDictionaries"] : null;
      })
    );
  }

  configurationDictionaryValue$<T>(feature: string, key: string): Observable<T[]> {
    return this.feature$(feature).pipe(
      map((featureConfig: FeatureConfig | null) => {
        return featureConfig !== null
          ? (featureConfig["configurationDictionaries"]?.[key] ?? null)
          : null;
      })
    );
  }

  patientConsoleSettingsValue$(feature: string, key: string): Observable<Record<string, unknown>> {
    return this.feature$(feature).pipe(
      map((featureConfig: FeatureConfig | null) => {
        return featureConfig !== null ? featureConfig["patientConsoleSettings"][key] : null;
      })
    );
  }

  featurePermission$(feature: string): Observable<Record<string, string[]>> {
    return this.feature$(feature).pipe(
      map((featureConfig: FeatureConfig | null) => {
        return featureConfig !== null ? featureConfig["featurePermissions"] : null;
      })
    );
  }

  featurePermissions$(feature: string, permission: string): Observable<string[]> {
    return this.feature$(feature).pipe(
      map((permissions: Record<string, string[]> | null) => {
        if (!!permissions && Object.keys(permission).length > 0 && !!permissions[permission]) {
          return permissions[permission];
        }
        return [];
      })
    );
  }

  messages$(feature: string): Observable<Record<string, string>> {
    return this.feature$(feature).pipe(
      map((featureConfig: FeatureConfig | null) => {
        return featureConfig !== null ? featureConfig["messages"] : null;
      })
    );
  }

  featureFlags$(feature: string): Observable<Record<string, boolean> | null> {
    return this.feature$(feature).pipe(
      map((featureConfig: FeatureConfig | null) => {
        return featureConfig ? (featureConfig?.["featureFlags"] ?? null) : null;
      })
    );
  }

  featureEnabled$(feature: string): Observable<boolean> {
    return this.feature$(feature).pipe(
      map((featureConfig: FeatureConfig | null) => {
        return featureConfig !== null ? featureConfig["enabled"] : false;
      })
    );
  }

  featureFlagEnabled$(feature: string, flag: string): Observable<boolean> {
    return this.featureFlags$(feature).pipe(
      map((featureItem: any) => {
        if (!featureItem) return false;
        else if (featureItem[flag] === false) return false;
        else return featureItem[flag] === true;
      })
    );
  }

  featureSchema$(feature: string, schema: string): Observable<Record<string, unknown>> {
    return this.feature$(feature).pipe(
      map((featureConfig: FeatureConfig | null) => {
        const schemas: Record<string, unknown>[] = featureConfig?.schemas;
        const result: Record<string, unknown> | undefined = schemas.find(s => s.id === schema);
        return result ?? {};
      })
    );
  }

  featureLayouts$(feature: string): Observable<UiSchemaCollection | null> {
    return this.feature$(feature).pipe(
      map((featureConfig: FeatureConfig | null) => {
        return featureConfig?.layoutConfig ?? null;
      })
    );
  }

  filterCommon(): Observable<unknown> {
    return new Observable(observer => {
      return this.feature$(OrganizationFeatures.common).subscribe({
        next: f => {
          observer.next(f);
        },
        error: err => {
          observer.error(err);
        },
        complete: () => {
          observer.complete();
        }
      });
    });
  }
}
